import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  PhoneIncoming, 
  PhoneOutgoing, 
  PhoneMissed,
  RefreshCw,
  Filter,
  Download,
  Clock,
  Calendar,
  User,
  Users,
  UserPlus,
  MessageSquare,
  Voicemail,
  FileText,
  Play,
  X,
  Loader2,
  Pause,
  Lock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import AddContactModal from "@/components/AddContactModal";

import { API_URL } from '../utils/api';
const API = API_URL;

function DirectionIcon({ direction }) {
  const d = direction?.toLowerCase();
  if (d === 'inbound') return <PhoneIncoming className="w-4 h-4 text-emerald-500" />;
  if (d === 'outbound') return <PhoneOutgoing className="w-4 h-4 text-primary" />;
  return <PhoneMissed className="w-4 h-4 text-amber-500" />;
}

function StatusBadge({ status }) {
  const s = status?.toLowerCase();
  let color = "bg-zinc-500/20 text-zinc-400";
  
  if (s === 'ok' || s === 'success') color = "bg-emerald-500/20 text-emerald-500";
  else if (s === 'busy') color = "bg-amber-500/20 text-amber-500";
  else if (s === 'no answer' || s === 'error') color = "bg-red-500/20 text-red-500";
  
  return (
    <span className={`text-xs px-2 py-1 rounded ${color}`}>
      {status || 'Unknown'}
    </span>
  );
}

function formatDuration(seconds) {
  if (!seconds) return "0s";
  const secs = parseInt(seconds);
  const mins = Math.floor(secs / 60);
  const remainingSecs = secs % 60;
  if (mins === 0) return `${remainingSecs}s`;
  return `${mins}m ${remainingSecs}s`;
}

// Format date to local preference (DD/MM/YYYY for NZ)
function formatLocalDate(dateStr) {
  if (!dateStr) return "";
  try {
    // Parse YYYY-MM-DD format
    const [year, month, day] = dateStr.split('-');
    if (year && month && day) {
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
}

// Format time to 12-hour format
function formatLocalTime(timeStr) {
  if (!timeStr) return "";
  try {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  } catch {
    return timeStr;
  }
}

function formatDateTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleString('en-NZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function MyCalls() {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState("calls");
  const [records, setRecords] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [recordingsTotal, setRecordingsTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [recordingsError, setRecordingsError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [recordingsPage, setRecordingsPage] = useState(0);
  const [playingAudio, setPlayingAudio] = useState(null);
  
  // Voicemail state
  const [voicemails, setVoicemails] = useState([]);
  const [voicemailTotal, setVoicemailTotal] = useState(0);
  const [voicemailUnread, setVoicemailUnread] = useState(0);
  const [loadingVoicemail, setLoadingVoicemail] = useState(false);
  const [playingVoicemail, setPlayingVoicemail] = useState(null);
  const [voicemailAudioLoading, setVoicemailAudioLoading] = useState(false);
  const voicemailAudioRef = useRef(null);

  // Helper to format date as YYYY-MM-DD in local timezone
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get date range for preset
  // Add 1 day buffer to "to" date to account for NZ timezone (+13 hours ahead of UTC)
  const getDateRange = (preset) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const todayStr = formatDate(today);
    const tomorrowStr = formatDate(tomorrow);
    
    switch (preset) {
      case 'today':
        return { from: todayStr, to: tomorrowStr };
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { from: formatDate(yesterday), to: todayStr };
      }
      case 'last7days': {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { from: formatDate(weekAgo), to: tomorrowStr };
      }
      case 'last30days': {
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return { from: formatDate(monthAgo), to: tomorrowStr };
      }
      case 'last60days': {
        const twoMonthsAgo = new Date(today);
        twoMonthsAgo.setDate(twoMonthsAgo.getDate() - 60);
        return { from: formatDate(twoMonthsAgo), to: tomorrowStr };
      }
      default:
        return { from: '', to: '' };
    }
  };

  const defaultRange = getDateRange('last30days');
  
  const [filters, setFilters] = useState({
    direction: "all",
    status: "all",
    date_from: defaultRange.from,
    date_to: defaultRange.to,
    datePreset: "last30days"
  });

  // Add contact dialog state
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [newContactPhone, setNewContactPhone] = useState("");
  const [newContactName, setNewContactName] = useState("");
  const [newContactCompany, setNewContactCompany] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactNotes, setNewContactNotes] = useState("");
  const [newContactType, setNewContactType] = useState("personal"); // personal or company
  const [savingContact, setSavingContact] = useState(false);

  // Open add contact dialog with phone number pre-filled
  const openAddContact = (phoneNumber) => {
    setNewContactPhone(phoneNumber);
    setNewContactName("");
    setNewContactCompany("");
    setNewContactEmail("");
    setNewContactNotes("");
    setNewContactType("personal"); // Default to personal
    setAddContactOpen(true);
  };

  // Save new contact
  const saveNewContact = async () => {
    if (!newContactName.trim()) {
      toast.error("Please enter a name");
      return;
    }

    setSavingContact(true);
    try {
      const contactData = {
        name: newContactName.trim(),
        phone_number: newContactPhone,
        source: newContactType // "personal" or "company"
      };
      
      // Add optional fields if provided
      if (newContactCompany.trim()) contactData.company = newContactCompany.trim();
      if (newContactEmail.trim()) contactData.email = newContactEmail.trim();
      if (newContactNotes.trim()) contactData.notes = newContactNotes.trim();
      
      await axios.post(`${API}/contacts`, contactData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const typeLabel = newContactType === "company" ? "company" : "personal";
      toast.success(`Contact "${newContactName}" added as ${typeLabel} contact`);
      setAddContactOpen(false);
      // Refresh records to show the new caller name
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add contact");
    } finally {
      setSavingContact(false);
    }
  };

  useEffect(() => {
    if (activeTab === "calls") {
      fetchRecords();
    } else if (activeTab === "transcripts") {
      fetchRecordings();
    } else if (activeTab === "voicemail") {
      fetchVoicemails();
    }
  }, [page, filters, activeTab, recordingsPage]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: "50",
        skip: String(page * 50),
        my_calls: "true"  // Filter to user's own calls
      });
      
      if (filters.direction && filters.direction !== "all") params.append("direction", filters.direction);
      if (filters.status && filters.status !== "all") params.append("status", filters.status);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);

      const res = await axios.get(`${API}/cdr/records?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(res.data.records || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch records:", error);
      toast.error("Failed to load call history");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecordings = async () => {
    setLoadingRecordings(true);
    try {
      const res = await axios.get(`${API}/my-recordings?limit=20&skip=${recordingsPage * 20}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecordings(res.data.recordings || []);
      setRecordingsTotal(res.data.total || 0);
      setRecordingsError(null);
    } catch (error) {
      console.error("Failed to fetch recordings:", error);
      // Handle plan restriction errors gracefully
      if (error.response?.status === 403) {
        setRecordingsError(error.response?.data?.detail || "This feature requires a plan upgrade");
      } else {
        toast.error("Failed to load recordings");
        setRecordingsError("Failed to load recordings");
      }
    } finally {
      setLoadingRecordings(false);
    }
  };

  const fetchVoicemails = async () => {
    setLoadingVoicemail(true);
    try {
      const res = await axios.get(`${API}/voicemail/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoicemails(res.data.messages || []);
      setVoicemailTotal(res.data.total || 0);
      setVoicemailUnread(res.data.unread || 0);
    } catch (error) {
      console.error("Failed to fetch voicemails:", error);
      if (error.response?.status === 401) {
        toast.error("Unable to access voicemail. Please check your SIP credentials.");
      } else {
        toast.error("Failed to load voicemail");
      }
    } finally {
      setLoadingVoicemail(false);
    }
  };

  const playVoicemailAudio = async (messageId) => {
    if (playingVoicemail === messageId) {
      // Stop playing
      if (voicemailAudioRef.current) {
        voicemailAudioRef.current.pause();
      }
      setPlayingVoicemail(null);
      setVoicemailAudioLoading(false);
    } else {
      // Start playing new message
      setVoicemailAudioLoading(true);
      setPlayingVoicemail(messageId);
    }
  };

  // Set up audio output device when audio element is ready
  const handleVoicemailAudioRef = async (audioElement) => {
    voicemailAudioRef.current = audioElement;
    if (audioElement) {
      // Set output device to match user preferences
      const preferredSpeaker = localStorage.getItem('preferredSpeaker');
      if (preferredSpeaker && typeof audioElement.setSinkId === 'function') {
        try {
          await audioElement.setSinkId(preferredSpeaker);
          console.log('Voicemail audio output set to:', preferredSpeaker);
        } catch (error) {
          console.warn('Could not set voicemail audio output device:', error);
        }
      }
    }
  };

  const handleVoicemailCanPlay = () => {
    setVoicemailAudioLoading(false);
  };

  const handleVoicemailEnded = () => {
    setPlayingVoicemail(null);
    setVoicemailAudioLoading(false);
  };

  const playRecording = (voiceuri) => {
    if (playingAudio === voiceuri) {
      setPlayingAudio(null);
    } else {
      setPlayingAudio(voiceuri);
    }
  };

  const handleDatePresetChange = (preset) => {
    const range = getDateRange(preset);
    setFilters(prev => ({
      ...prev,
      datePreset: preset,
      date_from: range.from,
      date_to: range.to
    }));
    setPage(0);
  };

  const handleManualDateChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      datePreset: "custom"
    }));
    setPage(0);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="p-6 space-y-6" data-testid="my-calls-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">My Calls</h1>
          <p className="text-muted-foreground">Your personal call history, transcripts and voicemail</p>
        </div>
        <Button 
          variant="outline"
          onClick={() => { fetchRecords(); fetchRecordings(); fetchVoicemails(); }}
          disabled={loading || loadingRecordings || loadingVoicemail}
          className="gap-2"
          data-testid="refresh-mycalls-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading || loadingRecordings || loadingVoicemail ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="calls" className="gap-2" data-testid="tab-calls">
            <PhoneIncoming className="w-4 h-4" />
            Call History
          </TabsTrigger>
          <TabsTrigger value="transcripts" className="gap-2" data-testid="tab-transcripts">
            <FileText className="w-4 h-4" />
            Transcripts
            {recordingsError && (
              <Lock className="w-3 h-3 text-amber-500" />
            )}
          </TabsTrigger>
          <TabsTrigger value="voicemail" className="gap-2" data-testid="tab-voicemail">
            <Voicemail className="w-4 h-4" />
            Voicemail
            {voicemailUnread > 0 && (
              <span className="bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                {voicemailUnread}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Call History Tab */}
        <TabsContent value="calls" className="space-y-4 mt-4">
          {/* Filters */}
          <Card className="bg-card border-border/50">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Direction</Label>
                  <Select 
                    value={filters.direction} 
                    onValueChange={v => handleFilterChange("direction", v)}
                  >
                    <SelectTrigger className="bg-secondary/50 w-32" data-testid="filter-direction">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="inbound">Inbound</SelectItem>
                      <SelectItem value="outbound">Outbound</SelectItem>
                      <SelectItem value="onnet">Internal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Select 
                    value={filters.status} 
                    onValueChange={v => handleFilterChange("status", v)}
                  >
                    <SelectTrigger className="bg-secondary/50 w-32" data-testid="filter-status">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="ok">Answered</SelectItem>
                      <SelectItem value="no answer">Missed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Date Range</Label>
                  <Select 
                    value={filters.datePreset} 
                    onValueChange={handleDatePresetChange}
                  >
                    <SelectTrigger className="bg-secondary/50 w-40" data-testid="filter-date-preset">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="yesterday">Yesterday</SelectItem>
                      <SelectItem value="last7days">Last 7 Days</SelectItem>
                      <SelectItem value="last30days">Last 30 Days</SelectItem>
                      <SelectItem value="last60days">Last 60 Days</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <Input 
                    type="date"
                    value={filters.date_from}
                    onChange={e => handleManualDateChange("date_from", e.target.value)}
                    className="bg-secondary/50 w-36"
                  />
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <Input 
                    type="date"
                    value={filters.date_to}
                    onChange={e => handleManualDateChange("date_to", e.target.value)}
                    className="bg-secondary/50 w-36"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Call History Table */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading">
                  Your Calls ({total})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <TableHead className="text-muted-foreground">Direction</TableHead>
                      <TableHead className="text-muted-foreground">Date/Time</TableHead>
                      <TableHead className="text-muted-foreground">Contact</TableHead>
                      <TableHead className="text-muted-foreground">Number</TableHead>
                      <TableHead className="text-muted-foreground">Duration</TableHead>
                      <TableHead className="text-muted-foreground">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12">
                          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    ) : records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                          No calls found for the selected period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      records.map((record, idx) => {
                        // Determine the "other party" based on direction
                        const direction = (record.direction || '').toLowerCase();
                        const isInbound = direction === 'inbound' || direction === 'onnet';
                        const otherPartyNumber = isInbound ? record.a_party : record.b_party;
                        const otherPartyName = isInbound ? record.from_name : record.to_name;
                        
                        return (
                          <TableRow 
                            key={record.id || idx} 
                            className="border-border/30 hover:bg-secondary/30"
                            data-testid={`my-call-row-${idx}`}
                          >
                            <TableCell>
                              <DirectionIcon direction={record.direction} />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3 h-3 text-muted-foreground" />
                                <span className="font-mono text-sm">{formatLocalDate(record.date)}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Clock className="w-3 h-3 text-muted-foreground" />
                                <span className="font-mono text-xs text-muted-foreground">{formatLocalTime(record.time)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {otherPartyName ? (
                                <span className="text-sm">{otherPartyName}</span>
                              ) : (
                                <button
                                  onClick={() => openAddContact(otherPartyNumber)}
                                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 hover:underline"
                                >
                                  <UserPlus className="w-3 h-3" />
                                  Add
                                </button>
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-sm">{otherPartyNumber}</TableCell>
                            <TableCell className="font-mono text-sm">{formatDuration(record.seconds)}</TableCell>
                            <TableCell><StatusBadge status={record.status} /></TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {total > 50 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
                  <span className="text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= totalPages - 1}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transcripts Tab */}
        <TabsContent value="transcripts" className="mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Call Transcripts & AI Summaries ({recordingsTotal})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingRecordings ? (
                <div className="py-12 text-center">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : recordingsError ? (
                <div className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">Feature Unavailable</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    {recordingsError}
                  </p>
                  <p className="text-sm text-muted-foreground mt-4">
                    Contact your administrator to upgrade your plan.
                  </p>
                </div>
              ) : recordings.length === 0 ? (
                <div className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Transcripts Found</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Call transcripts will appear here when AI transcription is enabled for your calls.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recordings.map((rec, idx) => (
                    <Card key={rec.id || idx} className="bg-secondary/30 border-border/30">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3">
                              <DirectionIcon direction={rec.direction} />
                              <span className="font-medium">
                                {rec.other_name || rec.other_party}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDateTime(rec.date)}
                              </span>
                              {rec.duration && (
                                <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                                  {formatDuration(rec.duration)}
                                </span>
                              )}
                            </div>
                            
                            {rec.transcript && (
                              <div className="bg-background/50 p-3 rounded text-sm">
                                <p className="text-muted-foreground text-xs mb-1">Transcript:</p>
                                <p>{rec.transcript}</p>
                              </div>
                            )}
                            
                            {rec.callsummary && (
                              <div className="bg-background/50 p-3 rounded text-sm space-y-2">
                                <p className="text-muted-foreground text-xs mb-1 font-medium">AI Analysis:</p>
                                {/* Handle callsummary as an object */}
                                {typeof rec.callsummary === 'object' ? (
                                  <div className="space-y-2">
                                    {rec.callsummary.summary && (
                                      <p><span className="text-muted-foreground">Summary:</span> {rec.callsummary.summary}</p>
                                    )}
                                    {rec.callsummary.sentiment_score !== undefined && (
                                      <p><span className="text-muted-foreground">Sentiment:</span> {rec.callsummary.sentiment_score > 0 ? 'Positive' : rec.callsummary.sentiment_score < 0 ? 'Negative' : 'Neutral'} ({rec.callsummary.sentiment_score})</p>
                                    )}
                                    {rec.callsummary.call_categories && rec.callsummary.call_categories.length > 0 && (
                                      <p><span className="text-muted-foreground">Categories:</span> {rec.callsummary.call_categories.join(', ')}</p>
                                    )}
                                    {rec.callsummary.names && rec.callsummary.names.length > 0 && (
                                      <p><span className="text-muted-foreground">Participants:</span> {rec.callsummary.names.join(', ')}</p>
                                    )}
                                    {rec.callsummary.reminders && rec.callsummary.reminders.length > 0 && (
                                      <div>
                                        <span className="text-muted-foreground">Action Items:</span>
                                        <ul className="list-disc list-inside ml-2">
                                          {rec.callsummary.reminders.map((r, i) => <li key={i}>{r}</li>)}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p>{rec.callsummary}</p>
                                )}
                              </div>
                            )}
                            
                            {/* Only show audio player if voiceuri is a direct audio URL (not a portal link) */}
                            {rec.voiceuri && !rec.voiceuri.includes('portal.hero.co.nz') && (
                              <div className="mt-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => playRecording(rec.voiceuri)}
                                  className="gap-2"
                                >
                                  <Play className="w-4 h-4" />
                                  {playingAudio === rec.voiceuri ? 'Stop' : 'Play Recording'}
                                </Button>
                                
                                {playingAudio === rec.voiceuri && (
                                  <div className="mt-2">
                                    <audio 
                                      src={rec.voiceuri} 
                                      controls 
                                      autoPlay
                                      className="w-full"
                                      onEnded={() => setPlayingAudio(null)}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Pagination */}
                  {recordingsTotal > 20 && (
                    <div className="flex items-center justify-between pt-4">
                      <span className="text-sm text-muted-foreground">
                        Page {recordingsPage + 1} of {Math.ceil(recordingsTotal / 20)}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRecordingsPage(p => Math.max(0, p - 1))}
                          disabled={recordingsPage === 0}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRecordingsPage(p => p + 1)}
                          disabled={recordingsPage >= Math.ceil(recordingsTotal / 20) - 1}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voicemail Tab */}
        <TabsContent value="voicemail" className="mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Voicemail className="w-5 h-5" />
                  Voicemail Messages
                  {voicemailUnread > 0 && (
                    <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                      {voicemailUnread} new
                    </span>
                  )}
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchVoicemails}
                  disabled={loadingVoicemail}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingVoicemail ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingVoicemail ? (
                <div className="py-12 text-center">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mt-2">Loading voicemails...</p>
                </div>
              ) : voicemails.length === 0 ? (
                <div className="py-12 text-center">
                  <Voicemail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Voicemail Messages</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Your voicemail inbox is empty. New voicemail messages will appear here when callers leave messages.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {voicemails.map((vm) => (
                    <Card 
                      key={vm.id} 
                      className={`bg-secondary/30 border-border/30 ${!vm.is_read ? 'border-l-4 border-l-primary' : ''}`}
                      data-testid={`voicemail-item-${vm.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3 flex-wrap">
                              <Voicemail className={`w-4 h-4 ${!vm.is_read ? 'text-primary' : 'text-muted-foreground'}`} />
                              <span className="font-medium">
                                {vm.caller || 'Unknown Caller'}
                              </span>
                              {vm.caller_name && (
                                <span className="text-sm text-muted-foreground">
                                  ({vm.caller_name})
                                </span>
                              )}
                              {vm.duration && (
                                <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                                  {vm.duration}
                                </span>
                              )}
                              {!vm.is_read && (
                                <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">
                                  New
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Calendar className="w-3 h-3" />
                              <span>{formatDateTime(vm.date)}</span>
                            </div>
                            
                            {vm.subject && vm.subject !== "[No Subject]" && (
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {vm.subject}
                              </p>
                            )}
                            
                            {/* Audio Player */}
                            <div className="mt-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => playVoicemailAudio(vm.id)}
                                className="gap-2"
                                data-testid={`play-voicemail-${vm.id}`}
                                disabled={voicemailAudioLoading && playingVoicemail === vm.id}
                              >
                                {voicemailAudioLoading && playingVoicemail === vm.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : playingVoicemail === vm.id ? (
                                  <Pause className="w-4 h-4" />
                                ) : (
                                  <Play className="w-4 h-4" />
                                )}
                                {voicemailAudioLoading && playingVoicemail === vm.id 
                                  ? 'Loading...' 
                                  : playingVoicemail === vm.id 
                                    ? 'Stop' 
                                    : 'Play Message'}
                              </Button>
                              
                              {playingVoicemail === vm.id && (
                                <div className="mt-2">
                                  {voicemailAudioLoading && (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      <span>Loading audio...</span>
                                    </div>
                                  )}
                                  <audio 
                                    ref={handleVoicemailAudioRef}
                                    src={`${API}/voicemail/audio/${vm.id}?token=${encodeURIComponent(token)}`}
                                    controls 
                                    autoPlay
                                    className={`w-full ${voicemailAudioLoading ? 'opacity-50' : ''}`}
                                    onCanPlay={handleVoicemailCanPlay}
                                    onEnded={handleVoicemailEnded}
                                    onError={() => {
                                      setVoicemailAudioLoading(false);
                                      toast.error('Failed to load voicemail audio');
                                    }}
                                  >
                                    Your browser does not support the audio element.
                                  </audio>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {voicemailTotal > 50 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Showing first 50 of {voicemailTotal} messages
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Contact Modal (shared component) */}
      <AddContactModal
        open={addContactOpen}
        onOpenChange={setAddContactOpen}
        prefillPhone={newContactPhone}
        onSaved={() => fetchMyCalls()}
      />
    </div>
  );
}
